import { Input, ModuleLayout, useModal } from '@billy/design-system'
import { trackError } from '@billy/track-error'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { DocumentStaticActionType } from '@views/documents/elements/DocumentStaticActions'
import { FileList, FileListItem, FileListItemType } from '@views/documents/elements/FileList'

import { ModalId } from '../../../../enums/modalId'
import { Scope } from '../../../../enums/scope'
import { useOrganizationViewUrl } from '../../../../hooks/routing/useOrganizationViewUrl'
import { isAuthorized } from '../../../../utils/isAuthorized'
import { DocumentPreviewModal } from '../../elements/DocumentPreviewModal'
import { DocumentsActionsBar } from '../../elements/DocumentsActionsBar'
import { DocumentsDropzone } from '../../elements/DocumentsDropzone'
import { MoveItemModal } from '../../elements/MoveItemModal'
import { useCopyDocumentOrFolderLink } from '../../hooks/useCopyDocumentOrFolderLink'
import { useDeleteItems } from '../../hooks/useDeleteItems'
import { useDmsUserRole } from '../../hooks/useDmsUserRole'
import { useDocumentDownload } from '../../hooks/useDocumentDownload'
import { useFetchFolderTree } from '../../hooks/useFetchFolderTree'
import { useIsClientDocumentsRoute } from '../../hooks/useIsClientDocumentsRoute'
import { useOpenDocumentIdFromStorage } from '../../hooks/useOpenDocumentIdFromStorage'
import { useRenameItem } from '../../hooks/useRenameItem'
import { DmsDocument } from '../../types'
import { getDocumentsRoutes } from '../../utils/getDocumentsRoutes'
import { notifyItemUnavailable } from '../../utils/notifyItemUnavailable'
import { FolderContentEmptyState } from './elements/FolderContentEmptyState'
import { FolderContentSideActions } from './elements/FolderContentSideActions'
import { useFolderContent } from './hooks/useFolderContent'
import { useFolderContentDragAndDrop } from './hooks/useFolderContentDragAndDrop'
import { useFolderTitleRename } from './hooks/useFolderTitleRename'
import { FolderContentParams } from './types/folderContentParams'

export const FolderContent = (): ReactElement => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [fileListKey, setFileListKey] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedDocument, setSelectedDocument] = useState<DmsDocument | null>(null)

  const [moveItems, setMoveItems] = useState<{ id: string; type: FileListItemType }[]>([])
  const [closePreviewAfterMove, setClosePreviewAfterMove] = useState(false)
  const { t } = useTranslation()
  const { clearOpenDocumentId, openDocumentId } = useOpenDocumentIdFromStorage()
  const { open: openMoveItemModal } = useModal(ModalId.DocumentsMoveItemModal)
  const { open: openDeleteModal } = useModal(ModalId.DocumentsFileListDeleteModal)
  const isClientDocumentsRoute = useIsClientDocumentsRoute()
  const documentsRoutes = getDocumentsRoutes(isClientDocumentsRoute)
  const { url: documentsRootUrl } = useOrganizationViewUrl(documentsRoutes.getMainRoute())
  // react-router-v5-compat types every useParams() value as possibly undefined, unlike v5's
  // useParams<T>() which returned exactly T. The route this component is registered on (see Documents.tsx)
  // requires a non-optional :folderId segment, so a match here always carries a defined value.
  const { folderId } = useParams<FolderContentParams>() as FolderContentParams
  const { close: closePreviewModal, open: openPreviewModal } = useModal(ModalId.DocumentsPreviewModal)

  const {
    currentFolderName,
    documents,
    filterByName,
    isEmpty,
    isNotFound,
    isError,
    isLoading,
    items,
    navigateToFolder,
  } = useFolderContent()
  const { copyLink } = useCopyDocumentOrFolderLink()
  const { renameItem } = useRenameItem()
  const { organization } = useUserOrganization()
  const { downloadDocument } = useDocumentDownload()
  const { isBoWithCpa } = useDmsUserRole()
  const { tree } = useFetchFolderTree()
  const {
    draggedItem,
    handleFilesDropOnFolder,
    handleFilesSelect,
    handleItemDragEnd,
    handleItemDragStart,
    handleItemDrop,
    isUploading,
  } = useFolderContentDragAndDrop({ folderId })

  const {
    handleBlur: handleRenameTitleBlur,
    handleChange: handleRenameTitleChange,
    handleKeyDown: handleRenameTitleKeyDown,
    isRenaming: isRenamingTitle,
    startRenaming: handleRenameTitleStart,
    value: folderRenameValue,
  } = useFolderTitleRename({ currentFolderName: currentFolderName ?? '', folderId, renameItem })

  const filteredItems = useMemo(() => filterByName(searchQuery), [filterByName, searchQuery])

  const initMoveItemModal = useCallback(
    (items: { id: string; type: FileListItemType }[], options?: { closePreviewOnSuccess?: boolean }) => {
      setMoveItems(items)
      setClosePreviewAfterMove(options?.closePreviewOnSuccess ?? false)
      openMoveItemModal()
    },
    [openMoveItemModal],
  )

  const handleBulkMoveToFolder = useCallback(() => {
    const itemsToMove = items.filter((item) => selectedIds.includes(item.id)).map(({ id, type }) => ({ id, type }))
    initMoveItemModal(itemsToMove)
  }, [items, selectedIds, initMoveItemModal])

  const handleCheckedChange = useCallback(
    (ids: string[]) => {
      setSelectedIds(ids)
    },
    [setSelectedIds],
  )

  const handleDeleteSuccess = useCallback(() => {
    setSelectedIds([])
    setFileListKey((prev) => prev + 1)
  }, [])

  const { deleteBulkItems, deleteItem } = useDeleteItems({
    items,
    onSuccess: handleDeleteSuccess,
  })

  const handleDownload = useCallback(
    (id: string) => {
      const targetDocument = documents.find((document) => document.id === id)
      if (targetDocument && organization?.id) {
        downloadDocument(organization.id, targetDocument.id, targetDocument.name).catch(trackError)
      }
    },
    [documents, downloadDocument, organization?.id],
  )

  const singleSelectedDocument = useMemo(() => {
    if (selectedIds.length !== 1) {
      return null
    }

    return filteredItems.find((item) => item.id === selectedIds[0] && item.type === 'document') ?? null
  }, [selectedIds, filteredItems])

  const handleBulkDelete = useCallback(() => {
    deleteBulkItems(selectedIds)
  }, [deleteBulkItems, selectedIds])

  const handleBulkDownload = useMemo(
    () => (singleSelectedDocument ? () => handleDownload(singleSelectedDocument.id) : undefined),
    [singleSelectedDocument, handleDownload],
  )

  const handleCopyLink = useCallback(
    (id: string) => {
      const item = filteredItems.find((listItem) => listItem.id === id)
      if (item) {
        // eslint-disable-next-line no-void -- copyLink handles its own errors.
        void copyLink(item.id, item.type)
      }
    },
    [copyLink, filteredItems],
  )

  // Bulk move/delete hit folder endpoints as soon as one folder is selected, so they need dms_folder_write.
  // Checked against the full (unfiltered) item list so a search query hiding the selected folder can't
  // re-enable these actions.
  const canMutateSelection = useMemo(
    () =>
      isAuthorized(Scope.DmsFolderWrite) ||
      !items.some((item) => item.type === 'folder' && selectedIds.includes(item.id)),
    [items, selectedIds],
  )

  const handleFolderMoveFromTopBar = useCallback(() => {
    initMoveItemModal([{ id: folderId, type: 'folder' }])
  }, [folderId, initMoveItemModal])

  const handleItemMove = useCallback(
    (id: string, type: FileListItemType) => {
      initMoveItemModal([{ id, type }])
    },
    [initMoveItemModal],
  )

  const handleItemMoveSuccess = useCallback(() => {
    if (closePreviewAfterMove) {
      closePreviewModal()
    }
  }, [closePreviewAfterMove, closePreviewModal])

  const handlePreviewModalAction = useCallback(
    (action: DocumentStaticActionType) => {
      if (action === DocumentStaticActionType.CopyLink && selectedDocument) {
        // eslint-disable-next-line no-void -- copyLink handles its own errors.
        void copyLink(selectedDocument.id, 'document')
      }
      if (action === DocumentStaticActionType.Delete && selectedDocument) {
        closePreviewModal()
        deleteItem(selectedDocument.id)
      }
      if (action === DocumentStaticActionType.Move && selectedDocument) {
        initMoveItemModal([{ id: selectedDocument.id, type: 'document' }], { closePreviewOnSuccess: true })
      }
    },
    [closePreviewModal, copyLink, deleteItem, initMoveItemModal, selectedDocument],
  )

  const handleRename = useCallback(
    (id: string, newName: string) => {
      const itemToRename = filteredItems.find((item) => item.id === id)
      if (itemToRename) {
        renameItem({ id, type: itemToRename.type, name: newName })
      }
    },
    [filteredItems, renameItem],
  )

  const handleRowClick = useCallback(
    (item: FileListItem) => {
      if (item.type === 'folder') {
        navigateToFolder(item.id)
        return
      }

      const targetDocument = documents.find((document) => document.id === item.id)
      if (targetDocument) {
        setSelectedDocument(targetDocument)
        openPreviewModal()
      }
    },
    [documents, openPreviewModal, navigateToFolder],
  )

  const handleSearchChange = useCallback(
    (query: string) => {
      setSearchQuery(query)
    },
    [setSearchQuery],
  )

  useEffect(() => {
    if (!openDocumentId || isLoading) {
      return
    }

    const targetDocument = documents.find((document) => document.id === openDocumentId)
    if (!targetDocument) {
      return
    }

    setSelectedDocument(targetDocument)
    openPreviewModal()
    clearOpenDocumentId()
  }, [clearOpenDocumentId, documents, isLoading, openDocumentId, openPreviewModal])

  useEffect(() => {
    if (isNotFound) {
      notifyItemUnavailable({ message: t('documents.folder_content.item_unavailable.message') })
    }
  }, [isNotFound, t])

  if ((isNotFound || isError) && documentsRootUrl) {
    return <Navigate to={documentsRootUrl} replace />
  }

  return (
    <>
      <ModuleLayout
        fullHeight
        fullWidth
        headerContent={
          isRenamingTitle ? (
            <Input
              autoFocus
              onBlur={handleRenameTitleBlur}
              onChange={handleRenameTitleChange}
              onKeyDown={handleRenameTitleKeyDown}
              value={folderRenameValue}
            />
          ) : undefined
        }
        sideActions={
          isBoWithCpa ? undefined : (
            <FolderContentSideActions
              isUploading={isUploading}
              onFilesSelect={handleFilesSelect}
              onMove={handleFolderMoveFromTopBar}
              onRenameStart={handleRenameTitleStart}
            />
          )
        }
        title={isRenamingTitle ? undefined : currentFolderName}
        withMenuOffset={false}
      >
        <DocumentsActionsBar
          isEmpty={isEmpty}
          isLoading={isLoading}
          onDelete={canMutateSelection ? openDeleteModal : undefined}
          onDownload={handleBulkDownload}
          onSearchChange={handleSearchChange}
          onMoveToFolder={canMutateSelection ? handleBulkMoveToFolder : undefined}
          placeholder={t('documents.folder_content.search_placeholder')}
          searchQuery={searchQuery}
          selectedIds={selectedIds}
        />

        <DocumentsDropzone isDisabled={isBoWithCpa} onFilesSelect={handleFilesSelect}>
          {filteredItems.length === 0 && !isLoading ? (
            <FolderContentEmptyState />
          ) : (
            <FileList
              key={fileListKey}
              draggedItem={draggedItem}
              folderTree={tree}
              isDragDisabled={isBoWithCpa}
              isLoading={isLoading}
              items={filteredItems}
              onBulkDelete={handleBulkDelete}
              onCheckedChange={handleCheckedChange}
              onCopyLink={handleCopyLink}
              onDelete={deleteItem}
              onDownload={handleDownload}
              onFilesDropOnFolder={handleFilesDropOnFolder}
              onItemDragEnd={handleItemDragEnd}
              onItemDragStart={handleItemDragStart}
              onItemDrop={handleItemDrop}
              onMove={handleItemMove}
              onRename={handleRename}
              onRowClick={handleRowClick}
              selectedCount={selectedIds.length}
              showCheckboxes={!isBoWithCpa}
              showContextMenu={!isBoWithCpa}
              showDirectDownloadButton={isBoWithCpa}
            />
          )}
        </DocumentsDropzone>

        {selectedDocument && (
          <DocumentPreviewModal
            id={ModalId.DocumentsPreviewModal}
            document={selectedDocument}
            onAction={handlePreviewModalAction}
            visibleActions={[
              DocumentStaticActionType.CopyLink,
              ...(isBoWithCpa ? [] : [DocumentStaticActionType.Move, DocumentStaticActionType.Delete]),
            ]}
          />
        )}
      </ModuleLayout>
      {moveItems.length > 0 && (
        <MoveItemModal
          id={ModalId.DocumentsMoveItemModal}
          items={moveItems}
          onMoveSuccess={handleItemMoveSuccess}
          sourceFolderId={folderId}
        />
      )}
    </>
  )
}
